import { loadImage } from './img'
import { tr } from '../i18n'

const W = 1080
const H = 1350

type CardInput = {
  before?: string | null
  after: string
  state: string
  /** дельта острых поворотов в пп (отрицательная = стало спокойнее) */
  deltaSharp?: number | null
}

/**
 * Шер-карточка 1080×1350: трансформация как артефакт (до/после),
 * лейбл состояния и маленькое лого вместо водяного знака-наказания.
 */
export async function renderShareCard(input: CardInput): Promise<Blob> {
  const t = tr()
  const c = document.createElement('canvas')
  c.width = W
  c.height = H
  const g = c.getContext('2d')!

  g.fillStyle = '#F6F5F1'
  g.fillRect(0, 0, W, H)

  const pad = 64
  const innerW = W - pad * 2

  const hasPair = !!input.before
  const imgs = hasPair
    ? await Promise.all([loadImage(input.before!), loadImage(input.after)])
    : [await loadImage(input.after)]

  const drawFramed = (img: HTMLImageElement, x: number, y: number, w: number, h: number, caption?: string) => {
    g.fillStyle = '#fff'
    g.strokeStyle = '#D8D6CE'
    g.lineWidth = 3
    roundRect(g, x, y, w, h, 24)
    g.fill()
    g.stroke()
    const sc = Math.min((w - 16) / img.width, (h - 16) / img.height)
    const iw = img.width * sc
    const ih = img.height * sc
    g.drawImage(img, x + (w - iw) / 2, y + (h - ih) / 2, iw, ih)
    if (caption) {
      g.fillStyle = '#5A5D66'
      g.font = "500 24px 'Golos Text', system-ui, sans-serif"
      g.textAlign = 'center'
      g.fillText(caption.toUpperCase(), x + w / 2, y + h + 40)
    }
  }

  const top = 150
  if (hasPair) {
    const gap = 24
    const w = (innerW - gap) / 2
    const h = Math.round(w * 1.1)
    drawFramed(imgs[0], pad, top, w, h, t.share.cardBefore)
    drawFramed(imgs[1], pad + w + gap, top, w, h, t.share.cardAfter)
  } else {
    const h = Math.round(innerW * 0.95)
    drawFramed(imgs[0], pad, top, innerW, h)
  }

  // Логотип
  g.textAlign = 'left'
  g.fillStyle = '#16181D'
  g.font = "600 34px 'Unbounded', system-ui, sans-serif"
  const [a, b, cc] = t.logo
  let x = pad
  g.fillText(a, x, 92)
  x += g.measureText(a).width
  g.fillStyle = '#3347E0'
  g.fillText(b, x, 92)
  x += g.measureText(b).width
  g.fillStyle = '#16181D'
  g.fillText(cc, x, 92)

  // Состояние + дельта
  const blockY = hasPair ? top + Math.round(((innerW - 24) / 2) * 1.1) + 110 : top + Math.round(innerW * 0.95) + 80
  g.font = "600 46px 'Unbounded', system-ui, sans-serif"
  g.fillStyle = '#16181D'
  wrapText(g, input.state, pad, blockY, innerW, 58)

  if (typeof input.deltaSharp === 'number') {
    const better = input.deltaSharp > 0
    g.fillStyle = better ? '#4C8A6B' : '#C97B2D'
    g.font = "600 40px 'Unbounded', system-ui, sans-serif"
    g.fillText(`${better ? '−' : '+'}${Math.abs(input.deltaSharp)} pp`, pad, H - 190)
    g.fillStyle = '#5A5D66'
    g.font = "400 26px 'Golos Text', system-ui, sans-serif"
    g.fillText(t.result.deltaSharp, pad, H - 148)
  }

  g.fillStyle = '#5A5D66'
  g.font = "500 26px 'Golos Text', system-ui, sans-serif"
  g.fillText(t.share.cardHash, pad, H - 72)
  g.textAlign = 'right'
  g.fillText(t.home.footer, W - pad, H - 72)

  return await new Promise<Blob>((resolve) => c.toBlob((bl) => resolve(bl!), 'image/png'))
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath()
  g.moveTo(x + r, y)
  g.arcTo(x + w, y, x + w, y + h, r)
  g.arcTo(x + w, y + h, x, y + h, r)
  g.arcTo(x, y + h, x, y, r)
  g.arcTo(x, y, x + w, y, r)
  g.closePath()
}

function wrapText(g: CanvasRenderingContext2D, text: string, x: number, y: number, maxW: number, lh: number) {
  const words = text.split(' ')
  let line = ''
  for (const w of words) {
    const test = line ? line + ' ' + w : w
    if (g.measureText(test).width > maxW && line) {
      g.fillText(line, x, y)
      y += lh
      line = w
    } else line = test
  }
  if (line) g.fillText(line, x, y)
}

export async function shareOrDownload(blob: Blob, title: string, text: string) {
  const file = new File([blob], 'linia.png', { type: 'image/png' })
  const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean }
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title, text })
      return 'shared'
    } catch {
      /* пользователь отменил — падаем в скачивание */
    }
  }
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'linia.png'
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 4000)
  return 'downloaded'
}
